import { useEffect, useRef, useState } from "react";
import { AudioLines, Download, Pause, Play } from "lucide-react";

import { parseBlogAudio } from "@/lib/blog-audio";

// A fixed, deterministic waveform pattern. Hardcoded (rather than randomized at
// render time) so server and client markup match and hydration stays stable.
const WAVEFORM = [
  0.32, 0.5, 0.68, 0.44, 0.82, 0.6, 0.38, 0.74, 0.92, 0.56, 0.4, 0.66, 0.85, 0.48, 0.7, 0.34, 0.58,
  0.78, 0.5, 0.9, 0.62, 0.42, 0.72, 0.54, 0.36, 0.64, 0.88, 0.46, 0.68, 0.8, 0.52, 0.38, 0.7, 0.6,
  0.84, 0.44, 0.56, 0.76, 0.48, 0.66, 0.9, 0.4, 0.58, 0.72, 0.5, 0.34, 0.62, 0.46,
];

const PLAYBACK_RATES = [1, 1.25, 1.5, 2];

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

export function MarkdownAudio({ source, title }: { source: string; title?: string }) {
  const audio = parseBlogAudio(source);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);
  const [rateIndex, setRateIndex] = useState(0);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;

    const onLoaded = () => setDuration(el.duration);
    const onTime = () => setCurrent(el.currentTime);
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onEnded = () => {
      setIsPlaying(false);
      setCurrent(0);
    };

    el.addEventListener("loadedmetadata", onLoaded);
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("play", onPlay);
    el.addEventListener("pause", onPause);
    el.addEventListener("ended", onEnded);
    // Metadata may already be available before the listener attaches.
    if (el.readyState >= 1) setDuration(el.duration);

    return () => {
      el.removeEventListener("loadedmetadata", onLoaded);
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("play", onPlay);
      el.removeEventListener("pause", onPause);
      el.removeEventListener("ended", onEnded);
    };
  }, []);

  if (!audio) return null;

  const togglePlay = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) void el.play();
    else el.pause();
  };

  const seekToFraction = (fraction: number) => {
    const el = audioRef.current;
    if (!el || !Number.isFinite(el.duration)) return;
    const clamped = Math.min(1, Math.max(0, fraction));
    el.currentTime = clamped * el.duration;
    setCurrent(el.currentTime);
  };

  const onTrackClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    seekToFraction((e.clientX - rect.left) / rect.width);
  };

  const cycleRate = () => {
    const next = (rateIndex + 1) % PLAYBACK_RATES.length;
    setRateIndex(next);
    if (audioRef.current) audioRef.current.playbackRate = PLAYBACK_RATES[next];
  };

  const playedFraction = duration > 0 ? current / duration : 0;

  return (
    <figure className="not-prose my-6 flex items-center gap-3 rounded-xl border border-border bg-card px-3 py-2.5 shadow-[var(--shadow-soft)] sm:gap-4 sm:px-4">
      <audio ref={audioRef} src={audio.src} preload="metadata" />

      <button
        type="button"
        onClick={togglePlay}
        aria-label={isPlaying ? "Pause audio brief" : "Play audio brief"}
        className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground transition-transform hover:scale-105 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-card"
      >
        {isPlaying ? (
          <Pause className="size-4" fill="currentColor" />
        ) : (
          <Play className="size-4 translate-x-px" fill="currentColor" />
        )}
      </button>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-primary">
          <AudioLines className="size-3.5 shrink-0" aria-hidden />
          <span className="shrink-0">Audio brief</span>
          {title && (
            <span
              className="truncate font-medium normal-case tracking-normal text-muted-foreground"
              title={title}
            >
              · {title}
            </span>
          )}
        </div>

        <div
          role="slider"
          tabIndex={0}
          aria-label="Seek"
          aria-valuemin={0}
          aria-valuemax={Math.round(duration)}
          aria-valuenow={Math.round(current)}
          aria-valuetext={`${formatTime(current)} of ${formatTime(duration)}`}
          onClick={onTrackClick}
          onKeyDown={(e) => {
            if (e.key === "ArrowRight") seekToFraction(playedFraction + 0.05);
            else if (e.key === "ArrowLeft") seekToFraction(playedFraction - 0.05);
            else return;
            e.preventDefault();
          }}
          className="mt-1.5 flex h-6 cursor-pointer items-center gap-[2px] rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-card"
        >
          {WAVEFORM.map((height, i) => {
            const played = i / WAVEFORM.length < playedFraction;
            return (
              <span
                key={i}
                aria-hidden
                className={`w-full rounded-full transition-colors ${
                  played ? "bg-primary" : "bg-primary/20"
                }`}
                style={{ height: `${Math.round(height * 100)}%` }}
              />
            );
          })}
        </div>
      </div>

      <div className="flex shrink-0 flex-col items-end gap-1">
        <span className="text-[11px] font-medium tabular-nums text-muted-foreground">
          {formatTime(current)} / {formatTime(duration)}
        </span>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={cycleRate}
            aria-label={`Playback speed ${PLAYBACK_RATES[rateIndex]}×`}
            className="rounded-full border border-border px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            {PLAYBACK_RATES[rateIndex]}×
          </button>
          <a
            href={audio.src}
            download
            aria-label="Download audio brief"
            title="Download"
            className="text-muted-foreground transition-colors hover:text-foreground"
          >
            <Download className="size-3.5" aria-hidden />
          </a>
        </div>
      </div>
    </figure>
  );
}
